import { Asset, NETWORKS, THOR } from "rujira.js";
import {
  canonicalAssetSymbol,
  canonicalChainFor,
  isSameCanonicalAsset,
} from "./canonicalAsset";

/** Assets funded by sending directly to a Rujira address rather than swapped in. */
const DIRECT_RUJIRA_DEPOSIT_SYMBOLS: ReadonlySet<string> = new Set([
  "LQDY",
  "RUJI",
  "RUNE",
  "TCY",
]);

const DEPOSIT_ASSET_PRIORITY: ReadonlyMap<string, number> = new Map(
  [
    "BTC",
    "ETH",
    "USDT",
    "USDC",
    "BNB",
    "XRP",
    "SOL",
    "DOGE",
    "ADA",
    "TRX",
    "AVAX",
    "LINK",
    "SUI",
    "LTC",
    "BCH",
    "DOT",
    "AAVE",
    "ATOM",
    "RUNE",
    "BUSD",
    "RUJI",
    "TCY",
    "LQDY",
    "AUTO",
  ].map((symbol, index): [string, number] => [symbol, index])
);

export const isDirectRujiraDepositSymbol = (symbol: string): boolean =>
  DIRECT_RUJIRA_DEPOSIT_SYMBOLS.has(canonicalAssetSymbol(symbol));

export const isDirectRujiraDepositAsset = (asset: Asset): boolean =>
  (asset.type === "SECURED" || asset.chain === THOR) &&
  isDirectRujiraDepositSymbol(asset.metadata.symbol);

/**
 * The exchange flow renders each asset's network label and gas token, both of
 * which throw for a network this app does not know. Only offer assets on
 * networks it supports, so a newly added pool cannot break the screen.
 */
export const isSupportedDepositNetwork = (asset: Asset): boolean =>
  NETWORKS.includes(asset.chain);

/** Select one preferred network representation for each canonical asset. */
export const canonicalDepositAssets = <T extends Asset>(
  assets: readonly T[]
): T[] =>
  assets.filter((candidate, index) => {
    const symbol = canonicalAssetSymbol(candidate.metadata.symbol);
    const matches = assets.filter((asset) =>
      isSameCanonicalAsset(asset.metadata.symbol, symbol)
    );
    const canonicalChain = canonicalChainFor(symbol);
    const preferred =
      matches.find(
        (asset) =>
          asset.metadata.symbol.toUpperCase() === symbol &&
          asset.chain === canonicalChain
      ) ||
      matches.find((asset) => asset.chain === canonicalChain) ||
      matches[0];

    return (
      candidate.asset === preferred?.asset &&
      assets.findIndex((asset) => asset.asset === candidate.asset) === index
    );
  });

export const compareDepositAssetPriority = (left: Asset, right: Asset) => {
  const priority = (asset: Asset) =>
    DEPOSIT_ASSET_PRIORITY.get(canonicalAssetSymbol(asset.metadata.symbol)) ??
    Number.MAX_SAFE_INTEGER;
  return (
    priority(left) - priority(right) ||
    left.metadata.symbol.localeCompare(right.metadata.symbol)
  );
};
